import { StrictMode, Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'
import { startAutoSchemeSync } from './lib/colorScheme'
import { markBoot } from './lib/perfBoot'
import { initGroundFromDom, setUiTheme, startRemoteUiPaletteSync, startRemoteUiThemeSync } from './lib/uiTheme'
import { startEnergyModeSync } from './lib/energyMode'
import { startSceneSync } from './lib/sceneMode'
import { readActiveMemberBot } from './member/memberSession'
import { resolveBotIdFromHref } from './lib/orderUtils'
import { readUrlView, shouldShowLandingInsteadOfLogin } from './member/myOrdersLogic'
import { hasAnyFreshLiffLogin } from './lib/liffSession'
import { authorizeOrderAccess } from './lib/orderAccess'
import { readJoinIntent } from './member/memberJoin'
import MemberLandingPage from './member/MemberLandingPage'
import MyOrdersPage from './member/MyOrdersPage'
import { readOfferingParams } from './offering/offeringLogic'
import { readShopParams } from './shop/shopLogic'
import { readMeritParams } from './merit/meritLogic'
import { readNamesParams } from './names/namesLogic'
import '../../aurora-luxury-theme.css'
import './styles/globals.css'
import './styles/glass-theme.css'
import './styles/member.css'
import './styles/member-hub.css'
import './styles/member-landing.css'
import './styles/smoky-theme.css'
import './styles/ground-theme.css'
import './styles/landing-v2.css'
import './styles/orders-v2.css'
import './styles/scene-v2.css'
import './styles/member-welcome.css'
import './styles/member-join.css'
import './styles/offering.css'
import './styles/shop.css'
import './styles/shop-themes.css'
import './styles/merit.css'

markBoot('ordercus:react-entry')
window.__orderCusAuthorizeOrder = authorizeOrderAccess

/** ?view=offering&dn=DN... = หน้าถวายทรัพย์ (ไม่ผ่านหน้าแรก/รายการสมาชิก) */
const offering = readOfferingParams(window.location.search)
/** หน้าแรกแนะนำบริการก่อนล็อกอิน; ?view=orders เปิดรายการสมาชิก */
/** ?view=shop[&item=<id>] = หน้าบูชา (แคตตาล็อก + ตะกร้า) */
const shop = readShopParams(window.location.search, window.location.pathname)
/** /merit · /donate · ?view=merit[&p=รอบ] = หน้าร่วมบุญแบบรอบ (รายละเอียด → กรอก → โอน/แนบสลิป → รายชื่อ) */
const merit = readMeritParams(window.location.search, window.location.pathname)
/** /names/?o=CO…&k=… = หน้ารายชื่อผู้ร่วมพิธี (รอบเดียวกับออร์เดอร์ · ธีมตามที่แอดมินตั้ง) */
const names = offering || shop || merit ? null : readNamesParams(window.location.search, window.location.pathname)
let entryView = offering || shop || merit || names ? null : readUrlView(window.location.search)
if (entryView === 'landing') {
  const activeBot = readActiveMemberBot(resolveBotIdFromHref())
  if (activeBot) {
    const url = new URL(window.location.href)
    url.searchParams.set('view', 'orders')
    if (activeBot === '2') url.searchParams.set('botId', '2')
    // Leave LINE callback parameters untouched until LIFF initialization finishes.
    if (!['code', 'state', 'liff.state', 'access_token', 'id_token'].some((key) => url.searchParams.has(key))) {
      window.history.replaceState(null, '', url.pathname + url.search + url.hash)
    }
    entryView = 'orders'
  }
}
// เว็บภายนอก + ไม่มี login ที่ยังสด + ไม่ได้กดเข้าสู่ระบบเอง → หน้าแรก ไม่เด้งไป LINE login (URL เก่า/แท็บกู้คืน/ออกจากระบบแล้ว)
if (entryView === 'orders') {
  const inLineApp = /Line\//i.test(navigator.userAgent || '') || window.location.href.includes('liff.line.me')
  const search = window.location.search
  if (shouldShowLandingInsteadOfLogin({ search, inLineApp, hasLogin: hasAnyFreshLiffLogin(resolveBotIdFromHref()) })) {
    const botParam = resolveBotIdFromHref() === '2' || new URLSearchParams(search).get('botId') === '2'
    // ลิงก์สมัครสมาชิก (join=1) — คงไว้ให้หน้าแรกแสดงหัวข้อสมัคร + ปุ่มเข้าสู่ระบบพากลับมาที่ฟอร์มสมัคร
    const landing = new URLSearchParams()
    if (botParam) landing.set('botId', '2')
    if (readJoinIntent(search)) landing.set('join', '1')
    window.history.replaceState(null, '', landing.toString() ? `/?${landing}` : '/')
    entryView = 'landing'
  } else if (new URLSearchParams(search).get('login') === '1' && !['code', 'state', 'liff.state'].some((key) => new URLSearchParams(search).has(key))) {
    // ใช้สิทธิ์ login=1 แล้ว — ตัดออกกัน bookmark/ประวัติบังคับ login ซ้ำ
    const url = new URL(window.location.href)
    url.searchParams.delete('login')
    window.history.replaceState(null, '', url.pathname + url.search + url.hash)
  }
}
// หน้าออร์เดอร์: เริ่มขอ token สิทธิ์ (LIFF init + memberApi orderAccess) ทันที ขนานกับโหลด Firebase SDK / chunk App
// เดิมรอจน ensureAuth ใน ordercus-firebase.js ถูกเรียก = ต่อคิวหลังทุกอย่าง · promise ถูกแคชใน orderAccess.ts จึงไม่ยิงซ้ำ
if (!entryView && window.__earlyOrderId) {
  void authorizeOrderAccess(String(window.__earlyOrderId)).catch(() => { /* ensureAuth จะลองใหม่และแสดงข้อผิดพลาดเอง */ })
}
const App = lazy(() => import('./App'))
const OfferingPage = lazy(() => import('./offering/OfferingPage'))
const ShopPage = lazy(() => import('./shop/ShopPage'))
const MeritPage = lazy(() => import('./merit/MeritPage'))
const NamesPage = lazy(() => import('./names/NamesPage'))

const isOrderVisible = () => document.getElementById('mainContent') !== null
if (entryView || offering || shop || merit) setUiTheme('glass')
else if (!names) startRemoteUiThemeSync(isOrderVisible)
initGroundFromDom()
// หน้ารายชื่อใส่ธีม/ชุดสีเองตามค่าที่แอดมินตั้งต่อพิธี (NamesPage)
if (!names) startRemoteUiPaletteSync(isOrderVisible)
startEnergyModeSync()
startSceneSync()
startAutoSchemeSync()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Suspense fallback={null}>
      {names ? <NamesPage orderId={names.orderId} k={names.k} /> : merit ? <MeritPage programId={merit.programId} /> : shop ? <ShopPage itemId={shop.item} incomingCart={shop.cart} autoPlace={!!shop.go && shop.cart.length > 0} /> : offering ? <OfferingPage dn={offering.dn} pay={offering.pay} /> : entryView ? (entryView === 'landing' ? <MemberLandingPage /> : <MyOrdersPage />) : <App />}
    </Suspense>
  </StrictMode>,
)

requestAnimationFrame(() => {
  markBoot('ordercus:first-raf')
})
